<script>
import StoreService from '@/services/StoreService'
import Tabs from '@/components/MarketplaceComponents/Tabs.vue'
import Breadcrumbs from '@/components/MarketplaceComponents/Breadcrumbs.vue'
import PageTitle from '@/components/MarketplaceComponents/PageTitle.vue'
import { mapState } from 'vuex'
import { createMeta } from '~/utils/meta'

export default {
  components: {
    Tabs,
    Breadcrumbs,
    PageTitle
  },

  async asyncData ({ $axios, route }) {
    const { data } = await StoreService.getMarketplaceAppByPath($axios, route.params.app)

    return {
      application: data?.data
    }
  },

  data () {
    return {
      application: null
    }
  },

  computed: {
    ...mapState('user', ['user']),

    tabs () {
      return [
        {
          id: 'game',
          title: this.$t('servicesInGame')
        },
        {
          id: 'app',
          title: this.$t('applications')
        },
        {
          id: 'social',
          title: this.$t('socialNetworks')
        },
        {
          id: 'offerwall',
          title: this.$t('offerwalls')
        }
      ]
    },

    activeTabId () {
      return this.application?.app_type || this.tabs[0].id
    },

    appPath () {
      return this.$route.params.app
    },

    currentTabTitle () {
      return this.tabs?.find(tab => tab.id === this.activeTabId)?.title
    },

    appName () {
      return this.application?.title || ''
    },

    breadcrumbs () {
      return [
        { title: this.$t('store'), to: '/' },
        {
          title: this.currentTabTitle,
          to: `/?tab=${this.activeTabId}`
        },
        {
          title: this.appName
        }
      ]
    },

    goBackPath () {
      return `/?tab=${this.activeTabId}`
    },

    categories () {
      return this.application?.categories
    },

    h1Title () {
      return this.$t('appItems', { name: this.appName })
    },

    isSeller () {
      return this.user?.is_seller
    },

    pagesCount () {
      if (!this.productsCount || this.productsCount === 0) {
        return 1
      }

      return Math.ceil(this.productsCount / this.pageSize)
    }
  },

  watch: {
    appPath () {
      this.loadApp()
    }
  },

  methods: {
    async loadApp () {
      const { data } = await StoreService.getMarketplaceAppByPath(this.$axios, this.appPath)

      this.application = data?.data
    },

    productDescription (product) {
      if (product.short_description && product.short_description !== '') {
        return product.short_description
      }

      return product.description
    },

    setActiveTab (tabId) {
      this.$router.push(this.localeLocation(`/?tab=${tabId}`))
    },

    appWithCategoryLink (category) {
      return this.localeLocation(`/marketplace/${this.appPath}/${category.seo_data.path}`)
    },

    addNewService () {
      this.$router.push(this.localeLocation(`/marketplace/add?type=${this.activeTabId}&app=${this.application.id}&category=${this.currentCategory}`))
    }
  },

  head () {
    return createMeta.call(this, {
      title: this.$t('applicationPageMetaTitle', {
        name: this.application?.title || '',
        categories: this.application?.categories?.map(c => this.$t(c.category_name))?.join(', ')
      }),
      description: this.$t('applicationPageMetaDescription', {
        name: this.application?.title || '',
        categories: this.application?.categories?.map(c => this.$t(c.category_name))?.join(', ')
      })
    })
  }
}
</script>

<template>
  <div :class="$style.page">
    <Tabs :tabs="tabs" :active-tab-id="activeTabId" @setActiveTab="setActiveTab" />

    <Breadcrumbs :list="breadcrumbs" />

    <div v-if="application" :class="$style.page__body">
      <PageTitle with-go-back :go-back-path="goBackPath" :title="h1Title" />

      <Button v-if="isSeller" is-primary :class="$style.seller" @click="addNewService">
        {{ $t('createService') }}
      </Button>

      <div :class="$style.tags">
        <NuxtLink v-for="category in categories" :key="category.id" :class="$style.tag" :to="appWithCategoryLink(category)">
          {{ $t(category.category_name.trim()) }} ({{ category.count }})
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
.page {
  width: 100%;

  &__body {
    padding: 16px;
  }
}

.seller {
  width: 100% !important;
  margin-bottom: 24px;
}

.tags {
  display: flex;
  flex-wrap: wrap;
}

.tag {
  padding: 8px 12px;
  background: $blue-grey50;
  color: $white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  margin-right: 8px;
  margin-bottom: 8px;
  border: 1px solid $blue-grey50;
  cursor: pointer;

  &:last-child {
    margin-right: 0;
  }
}
</style>
